import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { TREATMENT_PLAN_BASE_PATH } from '../../../../../constants';
import { areUserRolesIn, history } from '../../../../../helpers';
import { clientActions, treatmentPlanActions } from '../../../../../store';
import { CLIENT_SWITCH_CONFIRM_ROLES } from '../constants';
import { getTpRouteSegment, toClientOption } from '../utils';
import useTreatmentPlanV3HeaderState from './useTreatmentPlanV3HeaderState';

import type { SyntheticEvent } from 'react';
import type { AppDispatch } from '../../../../../store';
import type { IOption } from '../../../../../utils/types';
import type {
  ClientSelectDependencies,
  HeaderDialogController,
  UseClientSelectResult,
} from './types';

export default function useTreatmentPlanV3ClientSelect(
  dialog: HeaderDialogController,
): UseClientSelectResult {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const { user, clients, selectedClient, treatmentPlan } = useTreatmentPlanV3HeaderState();

  return useTreatmentPlanV3ClientSelectWithDependencies({
    dispatch,
    clientActions,
    planActions: treatmentPlanActions,
    getAccessTokenSilently,
    clients,
    selectedClient,
    treatmentPlan,
    userRoles: user?.roles ?? [],
    routeSegment: getTpRouteSegment(location.pathname),
    dialog,
  });
}

export function useTreatmentPlanV3ClientSelectWithDependencies({
  dispatch,
  clientActions: clientSelectActions,
  planActions,
  getAccessTokenSilently,
  clients,
  selectedClient,
  treatmentPlan,
  userRoles,
  routeSegment,
  dialog,
}: ClientSelectDependencies): UseClientSelectResult {
  const { activeDialog, openDialog, closeDialog } = dialog;
  const planClientId = treatmentPlan?.client_id;

  const applyClient = useCallback(
    (next: IOption | null) => {
      if (!next) {
        dispatch(clientSelectActions.clearSelectedClient());
        dispatch(planActions.clearTreatmentPlan());
        history.navigate(TREATMENT_PLAN_BASE_PATH);
        return;
      }
      const client = clients.find((item) => item.id === next.id);
      if (client?.id === undefined) return;
      dispatch(clientSelectActions.setSelected(client));
      dispatch(
        planActions.getTreatmentPlanByQuery({
          query: { clientId: client.id, with_activation_logs: true },
          getAccessTokenSilently,
          skipCurrentTpChange: planClientId === client.id,
          activeRoute: routeSegment,
          fromTP: true,
        }),
      );
    },
    [
      clientSelectActions,
      clients,
      dispatch,
      getAccessTokenSilently,
      planActions,
      planClientId,
      routeSegment,
    ],
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent, next: IOption | null) => {
      const prevId = selectedClient?.id ?? null;
      const nextId = next?.id ?? null;
      if (prevId !== null && nextId !== null && prevId === nextId) return;
      if (areUserRolesIn(userRoles, CLIENT_SWITCH_CONFIRM_ROLES) && selectedClient) {
        openDialog({ kind: 'switchClient', nextClient: next });
        return;
      }
      applyClient(next);
    },
    [applyClient, openDialog, selectedClient, userRoles],
  );

  const confirmSwitchClient = useCallback(() => {
    if (activeDialog?.kind !== 'switchClient') return;
    applyClient(activeDialog.nextClient);
    closeDialog();
  }, [activeDialog, applyClient, closeDialog]);

  return {
    value: selectedClient ? toClientOption(selectedClient) : null,
    options: clients.map(toClientOption),
    handleChange,
    confirmSwitchClient,
  };
}
